import React, { useState, useEffect } from 'react';
import {
  fetchInvoices,
  fetchInvoice,
  payInvoice,
  generateInvoiceFromPlan,
} from '../../services/billingApi';
import type { CompanyInvoice } from '../../services/billingApi';
import { useAuth } from '../../contexts/AuthContext';
import './Billing.css';
import { useI18n } from '../../contexts/I18nContext';

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pendente',
  paid: 'Pago',
  overdue: 'Vencido',
  cancelled: 'Cancelado',
  refunded: 'Reembolsado',
};

export const InvoicesList: React.FC = () => {
  const { t } = useI18n();
  const { isAuthenticated, token, isLoading: authLoading } = useAuth();
  const [invoices, setInvoices] = useState<CompanyInvoice[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<CompanyInvoice | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');

  useEffect(() => {
    if (authLoading || !isAuthenticated || !token) {
      return;
    }
    loadInvoices();
  }, [authLoading, isAuthenticated, token, statusFilter]);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      setError(null);
      const params: { limit: number; offset: number; status?: string } = {
        limit: 50,
        offset: 0,
      };
      if (statusFilter) params.status = statusFilter;
      const data = await fetchInvoices(params);
      setInvoices(data?.invoices ?? []);
      setTotal(data?.total ?? 0);
    } catch (err) {
      console.error('Error loading invoices:', err);
      setError(t('errors.loading'));
      setInvoices([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateFromPlan = async () => {
    try {
      setGenerating(true);
      setError(null);
      await generateInvoiceFromPlan();
      await loadInvoices();
    } catch (err) {
      console.error('Error generating invoice:', err);
      setError(t('errors.creating'));
    } finally {
      setGenerating(false);
    }
  };

  const handlePayInvoice = async (id: string) => {
    const paymentMethod = prompt(t('billing.paymentMethodPrompt'));
    if (!paymentMethod) return;

    try {
      await payInvoice(id, { payment_method: paymentMethod });
      loadInvoices();
      if (selectedInvoice?.id === id) {
        const updated = await fetchInvoice(id);
        setSelectedInvoice(updated);
      }
    } catch (err) {
      console.error('Error paying invoice:', err);
      alert(t('billing.paymentError'));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'green';
      case 'pending':
        return 'orange';
      case 'overdue':
        return 'red';
      case 'cancelled':
        return 'gray';
      default:
        return 'gray';
    }
  };

  if (authLoading || loading) {
    return <div className="billing-loading">{t('billing.loadingInvoices')}</div>;
  }

  return (
    <div className="billing-container">
      <div className="billing-header">
        <h2>{t('billing.tabs.invoices')}</h2>
        <div className="filters">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">{t('billing.allStatuses')}</option>
            <option value="pending">{t('financial.status.pending')}</option>
            <option value="paid">{t('financial.status.paid')}</option>
            <option value="overdue">Vencido</option>
            <option value="cancelled">{t('financial.status.cancelled')}</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="billing-error-state" role="alert">
          {error}
          <button type="button" className="btn-secondary" onClick={loadInvoices} style={{ marginLeft: 12 }}>{t('billing.retryLoad')}</button>
        </div>
      )}

      {invoices.length === 0 && !error ? (
        <div className="billing-empty-state billing-empty-state--card">
          <div className="billing-empty-icon" aria-hidden="true">
            📄
          </div>
          <p>
            <strong>{t('billing.noInvoices')}</strong>
          </p>
          <p>
            {statusFilter
              ? 'Não há faturas com o filtro selecionado. Tente outro status ou limpe o filtro.'
              : 'Ainda não existem faturas para esta empresa. Gere a primeira fatura a partir do plano ativo.'}
          </p>
          <div className="billing-actions-row">
            {!statusFilter && (
              <button
                type="button"
                className="btn-primary"
                onClick={handleGenerateFromPlan}
                disabled={generating}
              >
                {generating ? 'Gerando...' : 'Gerar fatura do plano'}
              </button>
            )}
            {statusFilter && (
              <button type="button" className="btn-secondary" onClick={() => setStatusFilter('')}>{t('billing.clearFilter')}</button>
            )}
          </div>
        </div>
      ) : (
        <>
          {total > 0 && (
            <p style={{ color: '#6b7280', fontSize: '0.9rem', margin: '0 0 16px' }}>
              {total} fatura{total !== 1 ? 's' : ''} encontrada{total !== 1 ? 's' : ''}
            </p>
          )}
          <div className="invoices-grid">
            {invoices.map((invoice) => (
              <div key={invoice.id} className="invoice-card" onClick={() => setSelectedInvoice(invoice)}>
                <div className="invoice-header">
                  <span className="invoice-number">{invoice.invoice_number}</span>
                  <span className={`status-badge ${getStatusColor(invoice.status)}`}>
                    {STATUS_LABELS[invoice.status] ?? invoice.status}
                  </span>
                </div>
                <div className="invoice-body">
                  <p>
                    <strong>{t('common.value')}</strong> R$ {invoice.total_amount.toFixed(2)}
                  </p>
                  <p>
                    <strong>Vencimento:</strong> {new Date(invoice.due_date).toLocaleDateString('pt-BR')}
                  </p>
                  {invoice.is_overdue && (
                    <p className="overdue-warning">Vencido há {invoice.days_overdue} dias</p>
                  )}
                </div>
                {invoice.status === 'pending' && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePayInvoice(invoice.id);
                    }}
                    className="btn-primary"
                  >
                    Pagar
                  </button>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {selectedInvoice && (
        <div className="modal-overlay" onClick={() => setSelectedInvoice(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{t('billing.invoiceDetails')}</h3>
            <p>
              <strong>{t('billing.number')}</strong> {selectedInvoice.invoice_number}
            </p>
            <p>
              <strong>{t('common.value')}</strong> R$ {selectedInvoice.total_amount.toFixed(2)}
            </p>
            <p>
              <strong>{t('common.status')}</strong>{' '}
              {STATUS_LABELS[selectedInvoice.status] ?? selectedInvoice.status}
            </p>
            <p>
              <strong>Vencimento:</strong>{' '}
              {new Date(selectedInvoice.due_date).toLocaleDateString('pt-BR')}
            </p>
            {selectedInvoice.items && selectedInvoice.items.length > 0 && (
              <div>
                <h4>Itens:</h4>
                <ul>
                  {selectedInvoice.items.map((item, idx) => (
                    <li key={idx}>
                      {item.description} - {item.quantity}x R$ {item.unit_price.toFixed(2)} = R${' '}
                      {item.total.toFixed(2)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <button type="button" onClick={() => setSelectedInvoice(null)}>{t('actions.close')}</button>
          </div>
        </div>
      )}
    </div>
  );
};
